// src/Components/UI/Alert/Alert.js
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./Alert.css";

const ICONS = {
  success: "✓",
  error:   "✕",
  warning: "⚠",
  info:    "ℹ",
};

// duration=0 → pas d'auto-dismiss
const DEFAULT_DURATION = { success: 4000, error: 6000, warning: 6000, info: 5000 };

// action?: { label: string, onClick: () => void }  — optional inline action button (e.g. "Réessayer")
// noToast?: bool — désactive le miroir global (pour un bandeau volontairement inline uniquement).
const Alert = ({ type = "info", title, message, onClose, duration, action, noToast = false }) => {
  const { t } = useTranslation("common");
  const [visible, setVisible]     = useState(true);
  const [leaving, setLeaving]     = useState(false);
  const [progress, setProgress]   = useState(100);

  const ms = duration !== undefined ? duration : DEFAULT_DURATION[type] ?? 5000;

  // Clé de corrélation Alert↔Toast : identifie le toast jumeau de CETTE Alert (type + contenu).
  // Fermer l'un ferme l'autre → jamais le même message affiché deux fois sous deux formes.
  const toastText = title ? `${title} — ${message}` : String(message ?? "");
  const corrKey = `alert:${type}:${toastText}`;

  // 🔝 PONT Alert → Toast GLOBAL : toute ERREUR (et avertissement) est AUSSI affichée en toast
  // portalisé au-dessus de TOUT (y compris une modale). Garantit la lisibilité même quand l'Alert
  // inline est rendu derrière une fenêtre au premier plan, et FAIT SURVIVRE le message à la
  // fermeture de la modale (le toast est indépendant du composant qui a fait setError). Une seule
  // fois par message (dédoublonnage Toast = type+message). `noToast` désactive le pont (écran dédié).
  useEffect(() => {
    if (noToast) return;
    if ((type === "error" || type === "warning") && message && typeof window !== "undefined" && window.__toast) {
      // Propage la durée ET la clé de corrélation → le toast jumeau partage le cycle de vie de l'Alert.
      window.__toast({ type, message: toastText, duration, key: corrKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, message, title, noToast, duration]);

  // 🔗 Le toast jumeau est fermé (✕, timer, clear) → l'Alert inline se masque AUSSI (pas de
  // « l'Alert reste alors que le toast a disparu »). On écoute l'évènement émis par le provider.
  useEffect(() => {
    if (noToast) return;
    const onToastDismissed = (e) => { if (e?.detail?.key === corrKey) setVisible(false); };
    window.addEventListener("gowele:toast-dismissed", onToastDismissed);
    return () => window.removeEventListener("gowele:toast-dismissed", onToastDismissed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corrKey, noToast]);

  // Ferme le toast jumeau quand l'Alert disparaît (fermeture manuelle, auto-dismiss, ou démontage).
  const closeTwinToast = () => {
    if (noToast) return;
    try { window.__toastRemoveByKey?.(corrKey); } catch (_) { /* no-op */ }
  };

  // Fermeture EXPLICITE de l'Alert (✕ manuel) ou AUTO-DISMISS (son timer) → on ferme AUSSI le toast
  // jumeau (pas de superposition : le même message ne reste pas sous une 2e forme).
  // ⚠️ On NE couple PAS le DÉMONTAGE : une Alert page-level derrière une modale se démonte quand le
  // parent fait setError(null) à la fermeture de la modale — dans ce cas le toast portalisé DOIT
  // survivre (c'est tout l'intérêt du pont). Le couplage ne vaut donc que pour une fermeture réelle.
  const dismiss = () => {
    setLeaving(true);
    closeTwinToast(); // le toast jumeau part en même temps que l'Alert fermée volontairement/au timer
    setTimeout(() => {
      setVisible(false);
      onClose?.();
    }, 300); // durée de l'animation de sortie
  };

  useEffect(() => {
    if (ms === 0) return; // désactivé

    // Barre de progression
    const start   = Date.now();
    const tick    = 50; // ms entre chaque tick
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / ms) * 100);
      setProgress(remaining);
      if (remaining === 0) clearInterval(interval);
    }, tick);

    const timer = setTimeout(dismiss, ms);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms]);

  if (!visible) return null;

  return (
    <div className={`ui-alert ui-alert--${type}${leaving ? " ui-alert--leaving" : ""}`}>
      <span className="ui-alert-icon">{ICONS[type]}</span>
      <div className="ui-alert-content">
        {title   && <h4 className="ui-alert-title">{title}</h4>}
        {message && <p  className="ui-alert-message">{message}</p>}
        {action  && (
          <button
            type="button"
            className="ui-alert-action"
            onClick={action.onClick}
          >
            {action.label}
          </button>
        )}
      </div>
      <button className="ui-alert-close" onClick={dismiss} aria-label={t('btn.close')}>✕</button>
      {ms > 0 && (
        <div className="ui-alert-progress">
          <div className="ui-alert-progress-bar" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};

export default Alert;
